import { useTranslation } from 'react-i18next';

function formatDate(isoDate, locale) {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleDateString(locale || undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

// Card de skill dentro do Gerenciador de Skills (Tarefa 3.2, specs/skills/plan.md)
// - nome, descricao, data e autor, mais o icone de editar.
//
// Tarefa 5.1 (FR-023): o card inteiro fica clicavel (onSelect) - o icone de
// acao (editar) intercepta o clique com stopPropagation para nao disparar
// essa acao junto.
//
// `[REVISADO em 2026-08-12, Tarefa 6.6, 3a rodada]`: onSelect passou a abrir
// "Detalhe da Skill" pra QUALQUER papel (dono ou nao), em vez de inserir a
// skill direto no chat so pro dono - decisao do usuario, pra unificar o
// comportamento do clique independente de quem esta vendo. Inserir no chat
// a partir do modal agora so acontece de dentro do Detalhe ("Experimentar no
// chat") - o dropdown "/" do chat (FR-005) e outro componente, nao muda.
//
// Tarefa 6.1 (FR-032, PBI 877): icone de editar (skills-edit-icon.svg,
// exportado do Figma node 101:600), so pro dono - showEditButton e decidido
// pelo pai com base na aba ativa.
//
// `[REMOVIDO nesta tarefa]`: os icones de download (Tarefa 5.3, FR-025) e de
// compartilhar (Tarefa 4.1/5.5, FR-027) sairam do card por decisao do
// usuario - as duas acoes passaram a viver so no cabecalho de "Detalhe da
// Skill" (SkillDetailView.js: skill-detail-download-button/
// skill-detail-share-button), acessivel clicando no proprio card.
//
// Tarefa 6.6 (FR-043, revisado em 2026-08-12): o toggle ativar/desativar mora
// no cabecalho de "Detalhe da Skill" (SkillDetailView.js), acionavel por
// qualquer usuario com acesso - nao ha mais nenhum estado "sem clique" no
// card (clicar sempre abre o Detalhe, ver comentario acima). `active ===
// false` so afeta a aparencia (`skill-card--inactive`, opacidade reduzida) -
// desativada continua clicavel, ja que e o unico jeito de chegar ao toggle
// pra reativar.
//
// Tarefa 1.1 (specs/updated_self_assistant/plan.md): `selectable` reaproveita
// o card para a aba "Skills" dedicada do assistente - troca o clique do card
// de "abrir Detalhe" (onSelect) para "vincular ao assistente" (onToggleSelect)
// e mostra um botao "+" (do lado direito, layout do Figma) em vez do icone de
// editar. Sem `selectable`, o comportamento do Gerenciador de Skills original
// fica inalterado. `[REVISADO nesta tarefa]`: o icone de compartilhar que
// existia no modo de selecao (herdado do antigo modal mode="link") foi
// removido por decisao do usuario - a lista de selecao agora mostra so as
// skills que o proprio usuario possui (sem necessidade de compartilhar pra
// usa-las neste assistente).
//
// Tarefa 12.1 (FR-079/FR-080/FR-081/FR-082, specs/skills/plan.md): badge com
// o nome do assistente vinculado, quando existir (`skill.assistants`, ja
// vindo do backend em ordem alfabetica e sem vinculos orfaos - assistente
// excluido nunca aparece aqui). Mais de um vinculo mostra o primeiro nome +
// "+N" com o restante revelado por tooltip nativo (title) ao passar o mouse.
export default function SkillCard({ skill, onSelect, onEdit, onShare, showEditButton, selectable, onToggleSelect }) {
    const { i18n } = useTranslation();
    const { skill_id, name, description, updated_at, owner, active, assistants } = skill;
    const isActive = active !== false;
    const linkedAssistants = assistants || [];

    return (
        <div
            className={`skill-card${isActive ? '' : ' skill-card--inactive'}${selectable ? ' skill-card--selectable' : ''}`}
            data-testid={`skill-card-${skill_id}`}
            onClick={selectable ? onToggleSelect : onSelect}
        >
            <div className="skill-card-header">
                <div className="skill-card-info">
                    <p className="skill-card-name" data-testid="skill-card-name" title={name}>/{name}</p>
                    <p className="skill-card-description" data-testid="skill-card-description" title={description}>{description}</p>
                </div>
                {selectable && (
                    <button
                        type="button"
                        className="skill-card-add-button"
                        data-testid="skill-card-add-button"
                        onClick={(event) => { event.stopPropagation(); onToggleSelect(); }}
                    >
                        <img src="/imagens/plus.svg" alt="" className="skill-card-add-icon" />
                    </button>
                )}
                {!selectable && showEditButton && (
                    <div className="skill-card-actions" onClick={(event) => event.stopPropagation()}>
                        {/* <button
                            type="button"
                            className="skill-card-edit-button"
                            data-testid="skill-card-edit-button"
                            onClick={onEdit}
                        >
                            <img src="/imagens/skills-edit-icon.svg" alt="" className="skill-card-edit-icon" />
                        </button> */}
                        <button
                            type="button"
                            className="skill-card-share-button"
                            data-testid="skill-card-share-button"
                            onClick={onShare}
                        >
                            <img src="/imagens/share.svg" alt="" className="skill-card-share-icon" />
                        </button>
                    </div>
                )}
            </div>
            {linkedAssistants.length > 0 && (
                <div className="skill-card-badges">
                    <span
                        className="skill-card-assistant-badge"
                        data-testid="skill-card-assistant-badge"
                        title={linkedAssistants.length > 1 ? linkedAssistants.slice(1).join(', ') : undefined}
                    >
                        {linkedAssistants[0]}
                        {linkedAssistants.length > 1 && ` +${linkedAssistants.length - 1}`}
                    </span>
                </div>
            )}
            <div className="skill-card-footer">
                <span className="skill-card-date" data-testid="skill-card-date">
                    {formatDate(updated_at, i18n.language)}
                </span>
                <span className="skill-card-author" data-testid="skill-card-author">
                    {owner?.name}
                </span>
            </div>
        </div>
    );
}
